'use client';

import React from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { MdFavorite, MdFavoriteBorder, MdShare } from 'react-icons/md';
import { FaLink } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { FaTelegram, FaGithub } from 'react-icons/fa';

import type { MarketplaceAppWithSecurityReport } from '../../types';

import Modal from '../Modal';
import styles from './MarketplaceAppModal.module.css';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  app: MarketplaceAppWithSecurityReport | null;
  isFavorite: boolean;
  onFavoriteClick: (appId: string) => void;
};

const MarketplaceAppModal: React.FC<Props> = ({
  isOpen,
  onClose,
  app,
  isFavorite,
  onFavoriteClick,
}) => {
  if (!app) return null;

  const handleShare = async() => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: app.title,
          text: app.shortDescription,
          url: app.url,
        });
      } catch (e) {
        // User cancelled or error
      }
    }
  };

  const siteHost = app.site ? (() => {
    try { return new URL(app.site).hostname.replace('www.', ''); } catch { return app.site; }
  })() : null;

  return (
    <Modal isOpen={ isOpen } onClose={ onClose } size="md">
      <div className={ styles.modalContent }>
        {/* Top section: logo left, info right */}
        <div className={ styles.topSection }>
          <div className={ styles.appLogo }>
            { app.logo ? (
              <img src={ app.logo } alt={ app.title }/>
            ) : (
              <div className={ styles.logoPlaceholder }>{ app.title[0] }</div>
            ) }
          </div>
          <div className={ styles.appInfo }>
            <div className={ styles.titleRow }>
              <h2 className={ styles.appTitle }>{ app.title }</h2>
              { app.url && <FiExternalLink size={ 18 } className={ styles.externalIcon }/> }
            </div>
            { app.author && (
              <p className={ styles.author }>By { app.author }</p>
            ) }
            <div className={ styles.actions }>
              { app.url && (
                <a
                  href={ app.url }
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ styles.launchButton }
                >
                  Launch app
                </a>
              ) }
              <button
                className={ styles.iconButton }
                onClick={ () => onFavoriteClick(app.id) }
              >
                { isFavorite ? (
                  <MdFavorite size={ 18 } color="#ef4444"/>
                ) : (
                  <MdFavoriteBorder size={ 18 }/>
                ) }
              </button>
              <button
                className={ styles.iconButton }
                onClick={ handleShare }
              >
                <MdShare size={ 18 }/>
              </button>
            </div>
          </div>
        </div>

        {/* Description */}
        { (app.description || app.shortDescription) && (
          <p className={ styles.description }>{ app.description || app.shortDescription }</p>
        ) }

        {/* Bottom: categories left, links right */}
        <div className={ styles.bottomSection }>
          <div className={ styles.categories }>
            { app.categories?.map((category) => (
              <span key={ category } className={ styles.category }>{ category }</span>
            )) }
          </div>
          <div className={ styles.links }>
            { app.site && (
              <a href={ app.site } target="_blank" rel="noopener noreferrer" className={ styles.siteLink }>
                <FaLink size={ 12 }/>
                { siteHost }
              </a>
            ) }
            { app.twitter && (
              <a href={ app.twitter } target="_blank" rel="noopener noreferrer" className={ styles.socialIcon }>
                <FaXTwitter size={ 16 }/>
              </a>
            ) }
            { app.telegram && (
              <a href={ app.telegram } target="_blank" rel="noopener noreferrer" className={ styles.socialIcon }>
                <FaTelegram size={ 16 }/>
              </a>
            ) }
            { app.github && (
              <a
                href={ Array.isArray(app.github) ? app.github[0] : app.github }
                target="_blank"
                rel="noopener noreferrer"
                className={ styles.socialIcon }
              >
                <FaGithub size={ 16 }/>
              </a>
            ) }
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MarketplaceAppModal;
